import { Injectable, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Router } from '@angular/router';
import { interval, Observable, Observer, throwError, Subscription } from 'rxjs';

import { Utils } from '../fork-common-controls/utils.service';
import { AuthenticationApi } from '../../../../generated/internalApi/api/AuthenticationApi';
import { SessionApi } from '../../../../generated/internalApi/api/SessionApi';
import { BorrowerService } from '../services/borrower/borrower.service';
import { MixpanelService } from '../services/mixpanel.service';
import { SessionService } from './session/shared/session/session-service/session.service';
import { SessionStorageService } from './session/shared/session/session-storage/session-storage.service';
import { SessionDataModel } from './session/shared/session/models/session-data.model';
import {
  AuthRefreshTokenData,
  AuthRefreshTokenResponse,
  LoginSsoRequest,
  LoginSsoResponse,
  UnifiedLoginRequest,
  UnifiedLoginResponse,
  UnifiedLoginResponseData,
  ValidateTokenResponse
} from '../models';

@Injectable()
export class AuthService {
  private authServiceChangeSource = new Subject<boolean>();
  private renewTimeout: any;

  // Log a User out after x Minutes
  private activityObserver: Observer<any> = {
    next: (expires) => {
      this.resetInactiveTimeout(expires);
    },
    error: err => err,
    complete: () => { }
  };

  private inactivityWarning: number = 10 /* in minutes */;
  private inactivityTimeout: number = 30 /* in minutes */;
  public inactivityTimer: Subject<any>;
  public sessionTimeoutWarningListener: Subscription;
  private timestampOfTimeout: number = null;
  private timestampOfWarning: number = null;

  public authServiceChange$ = this.authServiceChangeSource.asObservable();

  constructor(
    private authenticationApi: AuthenticationApi,
    private sessionApi: SessionApi,
    private borrowerService: BorrowerService,
    private mixpanelService: MixpanelService,
    private router: Router,
    private sessionService: SessionService) {

    this.inactivityTimer = new Subject<void>();
  }

  public get authToken(): string {
    return this.sessionService.data.authData.token;
  }

  public authenticateSpouseCoBorrower(emailAddress: string, password: string): Observable<boolean> {
    const requestData = {
      emailAddress,
      password
    } as UnifiedLoginRequest;

    const token = '';
    return this.authenticationApi.authenticationLoginUnified(requestData, token)
      .map((response: UnifiedLoginResponse) => !!response.data.token)
      .catch(err => {

        return throwError(err);
      });
  }

  public authenticateCommon(username: string, password: string): Observable<UnifiedLoginResponseData> {
    let requestData = {
      emailAddress: username,
      password: password
    } as UnifiedLoginRequest;

    const token = '';
    return this.authenticationApi.authenticationLoginUnified(requestData, token)
      .map((response: UnifiedLoginResponse) => this.commonLoginResponseHandler(response, username))
      .catch((err) => {
        console.error(err);
        return throwError(err);
      });
  }

  public commonLoginResponseHandler(response: UnifiedLoginResponse, email: string): UnifiedLoginResponseData {

    this.mixpanelService.identify(btoa(email));

    const bearerToken = 'Bearer ' + response.data.token;

    // SessionService
    const data = response.data;
    const authData = this.sessionService.data.authData;

    if (authData) {
      authData.expires = data.expires;
      authData.expirationSeconds = data.expirationSeconds;
      authData.token = bearerToken;
    }

    const pbData = this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerData;
    pbData.email = email;
    pbData.borrowerGuid = data.mortgageBorrowerGuid;

    this.sessionService.personalLoanSession.data.borrowerData.borrowerGuid = data.personalBorrowerGuid;

    if (data.personalBorrowerGuid) {
      this.borrowerService.getUnifiedBorrower().subscribe(borrower => {
        pbData.firstName = borrower.firstName;
        pbData.lastName = borrower.lastName;
      });
    }

    this.sessionService.isActive
      .map((session: SessionDataModel) => session.authData.expires)
      .distinctUntilChanged()
      .subscribe(this.activityObserver);

    this.configureSessionTimeoutWarning();

    return data;
  }

  private renewToken() {
    if (this.sessionService.data.authData.token) {
      this.authenticationApi.authenticationRefreshToken(this.sessionService.data.authData.token)
        .map(response => this.renewResponseHandler(response)).subscribe();
    }
  }

  private seedTimer(expirationSeconds: number): void {
    clearTimeout(this.renewTimeout);
    this.renewTimeout = setTimeout(() => this.renewToken(), Math.max(30, expirationSeconds - 60) * 1000);
  }

  renewResponseHandler(response: AuthRefreshTokenResponse): AuthRefreshTokenData {

    const data = response.data;
    const bearerToken = 'Bearer ' + data.token;

    const authData = this.sessionService.data.authData;
    authData.expires = data.expires;
    authData.expirationSeconds = data.expirationSeconds;
    authData.token = bearerToken;

    this.seedTimer(data.expirationSeconds);

    return data;
  }

  private getSessionServiceDataString(): string {
    return Utils.JSONStringify({
      authenticated: this.sessionService.authenticated,
      data: this.sessionService.data
    });
  }

  public logout(): void {
    this.sessionService.resetLoanApplication();

    this.cleanupSessionTimeoutWarning();

    // @TODO do we need this any more?
    // it looks like it _should_ refresh the Bearer token,
    // but doesn't seem to do anything.
    clearTimeout(this.renewTimeout);
    this.renewTimeout = null;

    this.sessionService.navigateToUla('login').then(redirected => {
      if (!redirected) {
        this.router.navigate(['/login']);
      }
    });

    this.authServiceChangeSource.next(false);
  }

  /**
   * @NOTE
   * This method is called on every update to sessionStorage
   * exercise extreme caution when calling this, as it would
   * be quite easy to create an endless loop.
   * @param expires: Datestring
   */
  private resetInactiveTimeout(expires: Date) {
    const timestamp = Date.parse(expires.toString());

    this.timestampOfTimeout = timestamp;
    this.timestampOfWarning = this.timestampOfTimeout - (this.inactivityWarning * 60000);
  }

  public stayLoggedIn() {
    this.renewToken();
    this.configureSessionTimeoutWarning();
  }

  // CLPBP-33 handle ULA transfer
  public loginWithToken(token: string, loanId: string, callback: (bwrId: string) => void, fail: () => void): void {
    if (loanId) {
      // set personal loan session's loanApplication id
      this.sessionService.personalLoanSession.data.loanApplicationGuid = loanId;
      // bearer token, just validate by making call to load token information
      this.authenticationApi.authenticationValidateToken('Bearer ' + token)
        .map(response => response.data)
        .subscribe(data => {
          // Personal only call - so no mortgage data
          const { authData } = this.sessionService.data;
          const { primaryBorrowerData } = this.sessionService.mortgageSession.data.borrowerData;
          const { borrowerData } = this.sessionService.personalLoanSession.data;

          // this.sessionService.mortgageSession.data.mortgageId = undefined;
          // primaryBorrowerData.borrowerId = undefined;
          // primaryBorrowerData.borrowerGuid = undefined;

          borrowerData.borrowerGuid = (data.borrowers && data.borrowers.length > 0 ? data.borrowers[0] : undefined);
          this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerSelected = !!borrowerData.borrowerGuid;

          authData.expires = data.expires;
          authData.expirationSeconds = data.expirationSeconds;
          authData.token = `Bearer ${data.token}`;
          if (callback) {
            callback(borrowerData.borrowerGuid);
          }
        },
        err => {
          console.warn('Unable to validate auth token from ULA', err);
          if (fail) {
            fail();
          }
        });
    } else {
      // sso token
      console.log('loginWithToken sso');
      const request: LoginSsoRequest = { ssoToken: token };
      this.loginWithSSOToken(request, callback);
    }
  }

  public loginWithSSOToken(request: LoginSsoRequest, callback?: Function): void {
    this.authenticationApi.authenticationLoginSso(request, this.authToken)
      .map(response => response.data)
      .subscribe((response: LoginSsoResponse) => {
        const { authData } = this.sessionService.data;
        const { primaryBorrowerData } = this.sessionService.mortgageSession.data.borrowerData;
        const { borrowerData } = this.sessionService.personalLoanSession.data;

        this.sessionService.mortgageSession.data.mortgageId = response.mortgageId;
        primaryBorrowerData.borrowerId = response.borrowerId;
        primaryBorrowerData.borrowerGuid = response.mortgageGuid;
        borrowerData.borrowerGuid = response.personalGuid;
        this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerSelected = true;
        authData.expires = response.expires;
        authData.expirationSeconds = response.expirationSeconds;
        authData.token = `Bearer ${response.token}`;

        if (callback) {
          callback();
        }
      }, console.error);
  }

  /**
   * To be called for any guard that requires authentication.
   * Sets up a intervaled check to see if we've reached a warning point
   */
  public configureSessionTimeoutWarning(): void {
    if (this.sessionTimeoutWarningListener instanceof Subscription) {
      return;
    }

    /**
     * If the user refreshed, this component will lose it's memory of timestamp
     * fortunately the sessionservice stores the token expiry here, and we
     * can restore it's in-memory state this way
     */
    if (this.sessionService.data.authData.expires && !this.timestampOfTimeout) {
      this.resetInactiveTimeout(this.sessionService.data.authData.expires);
    }

    const observable = interval(1000 /* ms */);

    this.sessionTimeoutWarningListener = observable.subscribe(
      () => {
        if (!(Date.now() < this.timestampOfTimeout)) {
          this.mixpanelService.track('Time expired and user was logged out');
          this.logout();
        } else if (!(Date.now() < this.timestampOfWarning)) {
          this.mixpanelService.track('Session Timeout Warning Modal Shown');
          this.inactivityTimer.next();
          this.cleanupSessionTimeoutWarning();
        }
      }
    );
  }

  /**
   * Cleans up any session timeout warning related activities
   */
  private cleanupSessionTimeoutWarning(): void {
    if (this.sessionTimeoutWarningListener) {
      this.sessionTimeoutWarningListener.unsubscribe();
    }
  }
}
